/**
 * The partner's Scopes — one card per scope they offer (price range,
 * typical turnaround, jurisdictions, capacity) plus a dimmed
 * dashed "+" card inside the grid to add another. Controlled: the edit
 * page owns the list (it saves with the profile form).
 *
 * The scope vocabulary (`NP_SCOPES`) is a placeholder until the
 * definitive list is agreed; the card is scope-agnostic on purpose.
 */

import type { ReactNode } from 'react';

import {
  ActionIcon,
  Card,
  Group,
  MultiSelect,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';

import { AddCard } from '@/components/AddCard';
import { JURISDICTIONS } from '@/constants';
import {
  NP_CURRENCIES,
  NP_SCOPES,
  scopeLabel,
  type NetworkPartnerScope,
  type NetworkPartnerScopePayload,
} from '@/types/network-partner';
import { asNumberOrEmpty } from '@/utils/numberInput';

const JURISDICTION_OPTIONS = JURISDICTIONS.map((c) => ({
  value: c.alpha2code,
  label: `${c.name} (${c.alpha2code})`,
}));

export const DEFAULT_SCOPE_CURRENCY = 'GBP';

/** One scope card's form state (NumberInputs use '' for "unset"). */
export interface ScopeFormValue {
  /** Client-side list key — stable across re-renders and reorders. */
  key: string;
  scope: string;
  min_price: number | '';
  max_price: number | '';
  currency: string;
  average_tat_days: number | '';
  jurisdictions: string[];
  max_capacity: number | '';
}

let keySeq = 0;
function nextKey(): string {
  keySeq += 1;
  return `scope-${keySeq}`;
}

function toNumberOrEmpty(value: number | string | null | undefined): number | '' {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return asNumberOrEmpty(value);
}

export function scopeToFormValue(scope: NetworkPartnerScope): ScopeFormValue {
  return {
    key: nextKey(),
    scope: scope.scope,
    min_price: toNumberOrEmpty(scope.min_price),
    max_price: toNumberOrEmpty(scope.max_price),
    currency: scope.currency ?? DEFAULT_SCOPE_CURRENCY,
    average_tat_days: toNumberOrEmpty(scope.average_tat_days),
    jurisdictions: scope.jurisdictions,
    max_capacity: toNumberOrEmpty(scope.max_capacity),
  };
}

export function emptyScopeFormValue(scopeKey: string): ScopeFormValue {
  return {
    key: nextKey(),
    scope: scopeKey,
    min_price: '',
    max_price: '',
    currency: DEFAULT_SCOPE_CURRENCY,
    average_tat_days: '',
    jurisdictions: [],
    max_capacity: '',
  };
}

export function scopeFormValueToPayload(value: ScopeFormValue): NetworkPartnerScopePayload {
  const hasPrice = value.min_price !== '' || value.max_price !== '';
  return {
    scope: value.scope,
    min_price: value.min_price === '' ? null : value.min_price,
    max_price: value.max_price === '' ? null : value.max_price,
    // The API requires a currency whenever a price is set; without a
    // price it is noise.
    currency: hasPrice ? value.currency || null : null,
    average_tat_days: value.average_tat_days === '' ? null : value.average_tat_days,
    jurisdictions: value.jurisdictions.map((j) => j.toUpperCase()),
    max_capacity: value.max_capacity === '' ? null : value.max_capacity,
  };
}

export type ScopeFieldErrors = Partial<Record<'scope' | 'max_price' | 'currency', string>>;

/** Client-side mirror of the API's scope rules — keyed by list index. */
export function validateScopes(values: ScopeFormValue[]): Record<number, ScopeFieldErrors> {
  const errors: Record<number, ScopeFieldErrors> = {};
  const seen = new Set<string>();
  values.forEach((value, index) => {
    const entry: ScopeFieldErrors = {};
    if (!value.scope) {
      entry.scope = 'Pick a scope';
    } else if (seen.has(value.scope)) {
      entry.scope = 'Duplicate scope';
    }
    seen.add(value.scope);
    if (value.min_price !== '' && value.max_price !== '' && value.min_price > value.max_price) {
      entry.max_price = 'Max price must be at least the min price';
    }
    if ((value.min_price !== '' || value.max_price !== '') && !value.currency) {
      entry.currency = 'Pick a currency for the price range';
    }
    if (Object.keys(entry).length > 0) {
      errors[index] = entry;
    }
  });
  return errors;
}

export function unusedScopeKeys(values: ScopeFormValue[]): string[] {
  const used = new Set(values.map((v) => v.scope));
  return NP_SCOPES.map((s) => s.value).filter((key) => !used.has(key));
}

interface NpScopesEditorProps {
  value: ScopeFormValue[];
  onChange: (next: ScopeFormValue[]) => void;
  errors?: Record<number, ScopeFieldErrors>;
  /** Banned partners are frozen: no edits, no add/remove. */
  readOnly?: boolean;
}

export function NpScopesEditor({
  value,
  onChange,
  errors = {},
  readOnly = false,
}: Readonly<NpScopesEditorProps>) {
  const unused = unusedScopeKeys(value);

  const patch = (index: number, changes: Partial<ScopeFormValue>) => {
    onChange(value.map((entry, i) => (i === index ? { ...entry, ...changes } : entry)));
  };
  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));
  const add = () => {
    if (unused.length > 0) {
      onChange([...value, emptyScopeFormValue(unused[0])]);
    }
  };

  if (value.length === 0 && readOnly) {
    return (
      <Text size="sm" c="dimmed">
        No scopes defined.
      </Text>
    );
  }

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" data-testid="np-scopes-grid">
      {value.map((entry, index) => (
        <ScopeCard
          key={entry.key}
          index={index}
          value={entry}
          scopeOptions={[entry.scope, ...unused].filter(Boolean)}
          errors={errors[index] ?? {}}
          readOnly={readOnly}
          onPatch={(changes) => patch(index, changes)}
          onRemove={() => remove(index)}
        />
      ))}
      {!readOnly && unused.length > 0 && (
        <AddCard
          label="Add scope"
          hint="Price range, turnaround, coverage and capacity for one kind of work"
          ariaLabel="Add a scope"
          onClick={add}
          data-testid="np-scope-add"
        />
      )}
    </SimpleGrid>
  );
}

interface ScopeCardProps {
  index: number;
  value: ScopeFormValue;
  scopeOptions: string[];
  errors: ScopeFieldErrors;
  readOnly: boolean;
  onPatch: (changes: Partial<ScopeFormValue>) => void;
  onRemove: () => void;
}

function ScopeCard({
  index,
  value,
  scopeOptions,
  errors,
  readOnly,
  onPatch,
  onRemove,
}: Readonly<ScopeCardProps>) {
  const numberField = (
    field: 'min_price' | 'max_price' | 'average_tat_days' | 'max_capacity',
    label: string,
    extra: { description?: string; decimals?: boolean; error?: ReactNode } = {}
  ) => (
    <NumberInput
      label={label}
      description={extra.description}
      min={0}
      allowNegative={false}
      allowDecimal={extra.decimals ?? false}
      decimalScale={extra.decimals ? 2 : 0}
      thousandSeparator={extra.decimals ? ',' : undefined}
      value={value[field]}
      onChange={(v) => onPatch({ [field]: asNumberOrEmpty(v) })}
      error={extra.error}
      disabled={readOnly}
      data-testid={`np-scope-${index}-${field}`}
    />
  );

  return (
    <Card withBorder radius="md" padding="md" data-testid={`np-scope-${index}`}>
      <Stack gap="sm">
        <Group align="flex-end" wrap="nowrap" gap="xs">
          <Select
            label="Scope"
            data={scopeOptions.map((key) => ({ value: key, label: scopeLabel(key) }))}
            value={value.scope}
            onChange={(v) => {
              if (v) {
                onPatch({ scope: v });
              }
            }}
            allowDeselect={false}
            error={errors.scope}
            disabled={readOnly}
            style={{ flex: 1 }}
            data-testid={`np-scope-${index}-key`}
          />
          {!readOnly && (
            <Tooltip label="Remove scope">
              <ActionIcon
                variant="subtle"
                color="red"
                size="lg"
                onClick={onRemove}
                aria-label={`Remove ${scopeLabel(value.scope)}`}
                data-testid={`np-scope-${index}-remove`}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
        <Group grow align="flex-start">
          {numberField('min_price', 'Min price', { decimals: true })}
          {numberField('max_price', 'Max price', { decimals: true, error: errors.max_price })}
          <Select
            label="Currency"
            data={NP_CURRENCIES}
            value={value.currency}
            onChange={(v) => onPatch({ currency: v ?? DEFAULT_SCOPE_CURRENCY })}
            allowDeselect={false}
            error={errors.currency}
            disabled={readOnly}
            data-testid={`np-scope-${index}-currency`}
          />
        </Group>
        <Group grow align="flex-start">
          {numberField('average_tat_days', 'Average TAT', {
            description: 'Typical turnaround, in days',
          })}
          {numberField('max_capacity', 'Max capacity', {
            description: 'Concurrent engagements',
          })}
        </Group>
        <MultiSelect
          label="Jurisdictions"
          description="Countries this scope covers"
          placeholder="Search country…"
          data={JURISDICTION_OPTIONS}
          searchable
          clearable
          value={value.jurisdictions}
          onChange={(codes) => onPatch({ jurisdictions: codes })}
          disabled={readOnly}
          data-testid={`np-scope-${index}-jurisdictions`}
        />
      </Stack>
    </Card>
  );
}
